import { Fragment, useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { classesHorsTanoEd, cleEleve, cleQuestion, etatDeLigne } from "../../../utils/importEvalNat";

/**
 * Le tableau de la fenêtre d'import des résultats nationaux (plan du 30/10/2026, étape 3) : une ligne par classe et
 * matière qui a son fichier de tests, avec ce que la vérification du serveur en dit. Les classes de la restitution
 * sans fichier de tests sortent du tableau (choix 1 validé) : en orange et nommées celles encore sans résultats, en
 * gris le seul nombre des classes déjà importées (au réimport, on ne redépose que deux ou trois fichiers).
 * Une classe prête à qui il manquera des résultats (lignes sans nom ni prénom, élèves incomplets) porte une seule
 * pastille orange ; un clic dessus ouvre le détail sous la classe (31/10/2026). De même pour ses élèves inconnus, en
 * rouge : le détail les nomme, avec les gestes de l'écran de fin, l'import des élèves et « Revérifier » (01/11/2026).
 * Les classes des fichiers absentes de TanoEd sortent aussi du tableau : des établissements n'y mettent que certaines
 * classes (01/11/2026). Une phrase grise les nomme une fois, avec le geste si elles y portent un autre nom (n° 32 du
 * diagnostic du 31/10/2026) ; si aucune classe des fichiers n'y est, c'est sûrement un souci de noms : leurs lignes
 * restent, en rouge. Une classe vide dans TanoEd garde sa ligne, en gris, et est nommée dessous avec l'import des
 * élèves et « Revérifier » (01/11/2026).
 */

const TONS = {
    ok: "bg-emerald-50 text-emerald-700",
    info: "bg-blue-50 text-blue-700",
    ambre: "bg-amber-50 text-amber-700",
    grave: "bg-red-50 text-red-700",
    rien: "bg-slate-100 text-slate-600",
};

export const accorder = (n, singulier, pluriel) => `${n} ${n > 1 ? pluriel : singulier}`;

/** Le nombre d'éléments par valeur d'un champ, dans l'ordre de première apparition. */
export const compterPar = (elements, champ) => elements.reduce(
    (compte, e) => compte.set(e[champ], (compte.get(e[champ]) || 0) + 1), new Map());

/** Des pastilles `[{ texte, ton }]`, dans l'ordre ; `children` se range après elles. */
export function Pastilles({ pastilles, children }) {
    return (
        <div className="flex flex-wrap gap-1.5 min-w-0">
            {pastilles.map(({ texte, ton }) => (
                <span key={texte} className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold whitespace-nowrap ${TONS[ton]}`}>
                    {texte}
                </span>
            ))}
            {children}
        </div>
    );
}

/**
 * Les élèves d'une ligne vérifiée à trancher, selon la réponse du délégué (`choix` : clé de question → numéro de
 * fiche, ou null pour « Non » et « Aucune ») : confirmés (une fiche choisie), en attente (pas encore de réponse), ou
 * restants, sans fiche (« Non », « Aucune », ou rien à leur proposer). `memes` : les inconnus et les élèves trouvés
 * dans une autre classe (« Même personne ? ») ; `lesquels` : plusieurs fiches à ce nom (« Lequel ? »).
 */
const trancherLesEleves = (ligne, verifie, { questions, choix }) => {
    const siennes = (questions || []).filter((q) => q.classe.id === ligne.classe.id && q.matieres.includes(ligne.matiere));
    const questionDe = (eleve) => siennes.find((q) => q.cle === cleQuestion(ligne.classe, cleEleve(eleve.nom, eleve.prenom)));
    const trancher = (eleves) => eleves.reduce((compte, eleve) => {
        const question = questionDe(eleve);
        const reponse = question ? choix?.[question.cle] : null;
        if (reponse) compte.confirmes += 1;
        else if (reponse === undefined) compte.enAttente += 1;
        else compte.restants.push({ nom: eleve.nom, prenom: eleve.prenom });
        return compte;
    }, { confirmes: 0, enAttente: 0, restants: [] });
    return { memes: trancher([...verifie.inconnus, ...verifie.ailleurs]), lesquels: trancher(verifie.aChoisir) };
};

/**
 * Les inconnus d'une classe prête : aucune fiche à leur nom, ou « Non » à « Même personne ? ». Ils ne seront pas
 * importés ; leur pastille les compte, et son détail les nomme (01/11/2026).
 */
export const inconnusDeLigne = (ligne, { verification, questions, choix }) => {
    const verifie = etatDeLigne(ligne, verification) === "prete" ? verification?.parLigne?.get(ligne.cle) : null;
    return verifie ? trancherLesEleves(ligne, verifie, { questions, choix }).memes.restants : [];
};

/**
 * Ce que dit une ligne : son état, puis, une fois vérifiée, ses élèves. Un élève à confirmer (trouvé dans une autre
 * classe, ou à un mot près) ou à choisir compte selon la réponse du délégué : confirmé, en attente, ou non rangé pour
 * « Lequel ? ». Les inconnus ont leur pastille à part, qui s'ouvre au clic (inconnusDeLigne).
 */
export const pastillesDeLigne = (ligne, { verification, verificationEnCours, questions, choix, restitutionDeposee }) => {
    const etat = etatDeLigne(ligne, verification);
    const pastilles = [];
    if (etat === "classe_inconnue") pastilles.push({ texte: "classe absente de TanoEd", ton: "grave" });
    if (etat === "classe_vide") pastilles.push({ texte: "aucun élève dans TanoEd", ton: "rien" });
    if (etat === "refusee") pastilles.push({ texte: verification.parLigne.get(ligne.cle).erreur, ton: "grave" });
    if (etat === "sans_restitution") {
        pastilles.push({ texte: restitutionDeposee ? "absente de la restitution" : "attend la restitution", ton: "rien" });
    }
    if (etat === "prete") {
        const verifie = verification?.parLigne?.get(ligne.cle);
        if (!verifie) {
            pastilles.push({ texte: verificationEnCours ? "vérification…" : accorder(ligne.eleves.length, "élève", "élèves"), ton: "rien" });
        } else {
            const { memes, lesquels } = trancherLesEleves(ligne, verifie, { questions, choix });
            const confirmes = memes.confirmes + lesquels.confirmes;
            const partis = verifie.partis?.length || 0;
            if (verifie.aCreer) pastilles.push({ texte: `${verifie.aCreer} à créer`, ton: "ok" });
            if (verifie.dejaImportes) pastilles.push({ texte: accorder(verifie.dejaImportes, "déjà importé", "déjà importés"), ton: "info" });
            if (confirmes) pastilles.push({ texte: accorder(confirmes, "confirmé", "confirmés"), ton: "ok" });
            if (memes.enAttente) pastilles.push({ texte: `${memes.enAttente} à confirmer`, ton: "ambre" });
            if (lesquels.enAttente) pastilles.push({ texte: `${lesquels.enAttente} à choisir`, ton: "ambre" });
            if (lesquels.restants.length) pastilles.push({ texte: accorder(lesquels.restants.length, "non rangé", "non rangés"), ton: "ambre" });
            if (partis) pastilles.push({ texte: partis > 1 ? `${partis} ont quitté l'établissement` : "1 a quitté l'établissement", ton: "rien" });
        }
    }
    if (ligne.doublons.length) pastilles.push({ texte: `${ligne.doublons.length} en double dans le fichier`, ton: "grave" });
    return pastilles;
};

/**
 * Le détail des résultats manquants d'une classe, sous sa ligne : les lignes sans nom ni prénom, jamais importées,
 * et où les trouver pour écrire le nom ; puis les élèves incomplets, avec ce qui leur manque.
 */
function DetailDesManquants({ ligne }) {
    const plusieursFichiers = new Set(ligne.sansNom.map((l) => l.fichier)).size > 1;
    return (
        <div role="row" className="px-3 py-2 border-t border-slate-100 bg-amber-50 text-xs text-slate-700">
            <div role="cell" className="flex flex-col gap-1">
                {ligne.sansNom.length > 0 ? (
                    <>
                        <b className="text-amber-700">Manquants : lignes sans nom ni prénom</b>
                        <ul className="list-disc pl-5">
                            {ligne.sansNom.map(({ fichier, feuille, numero }) => (
                                <li key={`${fichier}|${feuille}|${numero}`}>
                                    {fichier === "restitution" ? "Restitution" : "Tests"}, feuille « {feuille} », ligne {numero}
                                </li>
                            ))}
                        </ul>
                        <span className="text-slate-500">
                            {plusieursFichiers ? "Écrivez les noms dans les fichiers, puis redéposez-les." : "Écrivez les noms dans le fichier, puis redéposez-le."}
                        </span>
                    </>
                ) : null}
                {ligne.incomplets.length > 0 ? (
                    <>
                        <b className="text-amber-700">Incomplets</b>
                        <ul className="list-disc pl-5">
                            {ligne.incomplets.map(({ cle, nom, prenom, manque }) => (
                                <li key={cle}>{nom} {prenom}, sans {manque}</li>
                            ))}
                        </ul>
                    </>
                ) : null}
            </div>
        </div>
    );
}

/**
 * Le détail des inconnus d'une classe, sous sa ligne, avant l'import : les phrases de l'écran de fin, sauf la
 * dernière (on est déjà sur les fichiers, d'où « revérifiez »), l'import des élèves dans un nouvel onglet, et
 * « Revérifier » pour trouver l'élève une fois ajouté.
 */
function DetailDesInconnus({ eleves, onOuvrirImportEleves, onReverifier }) {
    return (
        <div role="row" className="px-3 py-2 border-t border-slate-100 bg-red-50 text-xs text-red-800">
            <div role="cell" className="flex flex-col gap-1.5">
                <div className="flex flex-col gap-0.5">
                    <b>{accorder(eleves.length, "élève introuvable", "élèves introuvables")} dans TanoEd</b>
                    <span>{eleves.map((e) => `${e.nom} ${e.prenom}`).join(" · ")}</span>
                </div>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                    <dt className="font-semibold">Nom écrit autrement dans TanoEd ?</dt>
                    <dd>Corrigez-le dans les fichiers, puis redéposez-les.</dd>
                    <dt className="font-semibold">Pas encore dans TanoEd ?</dt>
                    <dd>Passez par l'import des élèves, puis revérifiez.</dd>
                </dl>
                <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={onOuvrirImportEleves} className="inline-flex items-center gap-1 px-3 py-1 rounded-lg font-bold bg-white border border-red-300 text-red-700 hover:bg-red-100">
                        Ouvrir l'import des élèves
                        <ExternalLink className="w-3 h-3" aria-hidden="true" />
                    </button>
                    <button type="button" onClick={onReverifier} className="px-3 py-1 rounded-lg font-bold bg-white border border-slate-300 text-slate-700 hover:bg-slate-50">
                        Revérifier
                    </button>
                </div>
            </div>
        </div>
    );
}

const nomDeLigne = (ligne) => `${ligne.classe?.nom ?? ligne.classeFichier} ${ligne.matiere}`;

/**
 * Ce qui va au tableau : ni les classes sans fichier de tests (choix 1 validé), vides comprises, ni les classes absentes
 * de TanoEd (01/11/2026), sauf si aucune classe des fichiers n'y est.
 */
const auTableau = (ligne, etat, aucuneReconnue) => {
    if (etat === "classe_inconnue") return aucuneReconnue;
    if (etat === "classe_vide") return Boolean(ligne.fichierTests);
    return etat !== "sans_tests";
};

/**
 * `classesSansResultats` : les classes encore sans résultats nationaux, en clés « nom de classe|matière » (celles du
 * compteur du tableau de bord) ; elles seules sont nommées quand leur fichier de tests manque.
 * `onOuvrirImportEleves`, `onReverifier` : les deux boutons du détail des inconnus, et ceux des classes vides.
 */
function LignesImportEvalNat({ lignes, verification, verificationEnCours, questions, choix, restitutionDeposee, classesSansResultats, onOuvrirImportEleves, onReverifier }) {
    const [ouvertes, setOuvertes] = useState(() => new Set());
    // Aucune classe des fichiers dans TanoEd : sûrement un souci de noms, l'alerte rouge reste (01/11/2026).
    const aucuneReconnue = lignes.length > 0 && lignes.every((ligne) => !ligne.classe);
    const tableau = lignes.filter((ligne) => auTableau(ligne, etatDeLigne(ligne, verification), aucuneReconnue));
    const sansTests = lignes.filter((ligne) => etatDeLigne(ligne, verification) === "sans_tests");
    const attendent = sansTests.filter((ligne) => classesSansResultats?.has(`${ligne.classe.nom}|${ligne.matiere}`));
    const dejaImportees = sansTests.length - attendent.length;
    // Une classe absente de TanoEd, ou vide, est nommée une fois pour ses deux matières.
    const horsTanoEd = classesHorsTanoEd(lignes);
    const vides = [...new Set(lignes
        .filter((ligne) => etatDeLigne(ligne, verification) === "classe_vide")
        .map((ligne) => ligne.classe.nom))];
    const options ={ verification, verificationEnCours, questions, choix, restitutionDeposee };
    const basculer = (cle) => setOuvertes((avant) => {
        const apres = new Set(avant);
        if (apres.has(cle)) apres.delete(cle);
        else apres.add(cle);
        return apres;
    });

    return (
        <div className="flex flex-col gap-2">
            {tableau.length > 0 ? (
                <div role="table" aria-label="Une ligne par classe et matière" className="border border-slate-200 rounded-xl overflow-hidden">
                    <div role="row" className="grid grid-cols-[9rem_11rem_1fr] gap-3 px-3 py-2 bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                        <span role="columnheader">Classe</span>
                        <span role="columnheader">Fichiers</span>
                        <span role="columnheader">Élèves</span>
                    </div>
                    {tableau.map((ligne) => {
                        // Les résultats manquants, sur une classe prête seulement : une pastille, le détail au clic.
                        const manquants = etatDeLigne(ligne, verification) === "prete" ? ligne.manquants : 0;
                        const ouvert = manquants > 0 && ouvertes.has(ligne.cle);
                        // Les inconnus de même : la pastille les compte, le détail les nomme.
                        const inconnus = inconnusDeLigne(ligne, options);
                        const inconnusOuverts = inconnus.length > 0 && ouvertes.has(`${ligne.cle}|inconnus`);
                        return (
                            <Fragment key={ligne.cle}>
                                <div role="row" className="grid grid-cols-[9rem_11rem_1fr] gap-3 items-center px-3 py-2 border-t border-slate-100 text-sm">
                                    <span role="cell" className="font-bold text-slate-800">
                                        {ligne.classe?.nom ?? ligne.classeFichier} <span className="font-semibold text-slate-500">{ligne.matiere}</span>
                                    </span>
                                    <span role="cell" className="flex flex-wrap gap-2 text-xs font-bold">
                                        <span className={ligne.restitution ? "text-emerald-700" : "text-red-600"}>{ligne.restitution ? "✓" : "✗"} restitution</span>
                                        <span className={ligne.fichierTests ? "text-emerald-700" : "text-red-600"}>{ligne.fichierTests ? "✓" : "✗"} tests</span>
                                    </span>
                                    <span role="cell">
                                        <Pastilles pastilles={pastillesDeLigne(ligne, options)}>
                                            {inconnus.length > 0 ? (
                                                <button
                                                    type="button"
                                                    onClick={() => basculer(`${ligne.cle}|inconnus`)}
                                                    aria-expanded={inconnusOuverts}
                                                    className="inline-flex items-center gap-0.5 px-2.5 py-0.5 rounded-full text-xs font-bold whitespace-nowrap bg-red-50 text-red-700 ring-1 ring-inset ring-red-300 hover:bg-red-100"
                                                >
                                                    {accorder(inconnus.length, "inconnu", "inconnus")}
                                                    {inconnusOuverts ? <ChevronDown className="w-3 h-3" aria-hidden="true" /> : <ChevronRight className="w-3 h-3" aria-hidden="true" />}
                                                </button>
                                            ) : null}
                                            {manquants > 0 ? (
                                                <button
                                                    type="button"
                                                    onClick={() => basculer(ligne.cle)}
                                                    aria-expanded={ouvert}
                                                    className="inline-flex items-center gap-0.5 px-2.5 py-0.5 rounded-full text-xs font-bold whitespace-nowrap bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-300 hover:bg-amber-100"
                                                >
                                                    {accorder(manquants, "résultat manquant", "résultats manquants")}
                                                    {ouvert ? <ChevronDown className="w-3 h-3" aria-hidden="true" /> : <ChevronRight className="w-3 h-3" aria-hidden="true" />}
                                                </button>
                                            ) : null}
                                        </Pastilles>
                                    </span>
                                </div>
                                {inconnusOuverts ? <DetailDesInconnus eleves={inconnus} onOuvrirImportEleves={onOuvrirImportEleves} onReverifier={onReverifier} /> : null}
                                {ouvert ? <DetailDesManquants ligne={ligne} /> : null}
                            </Fragment>
                        );
                    })}
                </div>
            ) : null}
            {aucuneReconnue ? (
                <p className="text-sm font-semibold text-red-700">
                    {horsTanoEd.length > 1 ? "Introuvables" : "Introuvable"} dans TanoEd : {horsTanoEd.join(", ")}. Autre nom dans TanoEd ? Écrivez-le dans la colonne Classe des fichiers, puis redéposez-les.
                </p>
            ) : null}
            {vides.length > 0 ? (
                <div className="flex flex-col gap-1.5">
                    <p className="text-sm font-semibold text-amber-700">
                        {vides.join(", ")} : aucun élève dans {vides.length > 1 ? "ces classes" : "cette classe"} sur TanoEd. Importez d'abord {vides.length > 1 ? "leurs" : "ses"} élèves, puis revérifiez.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={onOuvrirImportEleves} className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-bold bg-white border border-slate-300 text-slate-700 hover:bg-slate-50">
                            Ouvrir l'import des élèves
                            <ExternalLink className="w-3 h-3" aria-hidden="true" />
                        </button>
                        <button type="button" onClick={onReverifier} className="px-3 py-1 rounded-lg text-xs font-bold bg-white border border-slate-300 text-slate-700 hover:bg-slate-50">
                            Revérifier
                        </button>
                    </div>
                </div>
            ) : null}
            {attendent.length > 0 ? (
                <p className="text-sm font-semibold text-amber-700">
                    {attendent.length > 1 ? "Attendent leur fichier de tests" : "Attend son fichier de tests"} : {attendent.map(nomDeLigne).join(", ")}.
                </p>
            ) : null}
            {horsTanoEd.length > 0 && !aucuneReconnue ? (
                <p className="text-sm text-slate-500">
                    Pas dans TanoEd : {horsTanoEd.join(", ")}. {horsTanoEd.length > 1 ? "Leurs" : "Ses"} résultats sont laissés de côté. Autre nom dans TanoEd ? Écrivez-le dans la colonne Classe des fichiers, puis redéposez-les.
                </p>
            ) : null}
            {dejaImportees > 0 ? (
                <p className="text-sm text-slate-500">
                    {accorder(dejaImportees, "classe déjà importée", "classes déjà importées")}, sans nouveau fichier de tests : rien ne change pour {dejaImportees > 1 ? "elles" : "elle"}.
                </p>
            ) : null}
        </div>
    );
}

export default LignesImportEvalNat;
